import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, UserPlus, UserRound, X } from 'lucide-react';
import { api } from '../../../lib/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Switch } from '../../ui/switch';
import { mensajeDe } from '../responder/utilidades';
import SelectorPersona, { Avatar } from './SelectorPersona';
import { REGLAS_ASIGNACION } from './constantes';
import './buzon.css';

/*
 * Configurar el buzón de casos de un formulario: categorías (con responsable y plazo), cómo se reparten
 * los casos, el equipo, y lo que recibe quien envía (folio, seguimiento, acuse). Solo quien edita.
 *
 *   <ConfigBuzon formId open onOpenChange onGuardado={(buzon) => …} />
 *
 * Guarda con PUT /forms/{id}/buzon; el servidor normaliza, quita a quien no es de la organización y,
 * al activar, convierte en casos las respuestas que ya había.
 */

const COLORES = ['#c05c6a', '#4b7bc0', '#3f9576', '#b07d2e', '#9c6ac8', '#0f8a9d', '#2563eb', '#b8577f'];

export default function ConfigBuzon({ formId, open, onOpenChange, onGuardado }) {
  const [carga, setCarga] = useState({ cargando: true, error: null });
  const [config, setConfig] = useState(null);
  const [personas, setPersonas] = useState({});
  const [preguntas, setPreguntas] = useState([]);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let vivo = true;
    setCarga({ cargando: true, error: null });
    api.get(`/forms/${formId}/buzon`)
      .then(({ data }) => {
        if (!vivo) return;
        setConfig(data.buzon);
        setPersonas(Object.fromEntries((data.personas || []).map((p) => [p.id, p])));
        setPreguntas(data.preguntas || []);
        setCarga({ cargando: false, error: null });
      })
      .catch((err) => vivo && setCarga({ cargando: false, error: mensajeDe(err, 'No se pudo cargar la configuración') }));
    return () => { vivo = false; };
  }, [open, formId]);

  const set = (campos) => setConfig((c) => ({ ...c, ...campos }));
  const conocer = (p) => p && setPersonas((m) => ({ ...m, [p.id]: p }));
  const setCategoria = (i, campos) => set({ categorias: config.categorias.map((c, j) => (j === i ? { ...c, ...campos } : c)) });
  const preguntasCategoria = useMemo(() => preguntas.filter((p) => p.opciones), [preguntas]);
  const preguntasCorreo = useMemo(() => preguntas.filter((p) => p.tipo === 'texto_corto'), [preguntas]);

  const categoriasDesdePregunta = () => {
    const p = preguntas.find((x) => x.id === config.campo_categoria);
    if (!p?.opciones?.length) return;
    set({
      categorias: p.opciones.slice(0, 20).map((texto, i) => {
        const previa = config.categorias.find((c) => c.nombre.toLowerCase() === texto.toLowerCase());
        return previa || { nombre: texto, color: COLORES[i % COLORES.length], responsable_id: null, dias_sla: null };
      }),
    });
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const cuerpo = { ...config, categorias: config.categorias.filter((c) => c.nombre.trim()) };
      const { data } = await api.put(`/forms/${formId}/buzon`, cuerpo);
      setConfig(data.buzon);
      toast.success(data.casos_creados ? `Buzón guardado · ${data.casos_creados} respuestas pasaron a ser casos` : 'Buzón guardado');
      onGuardado?.(data.buzon);
      onOpenChange(false);
    } catch (err) {
      toast.error(mensajeDe(err, 'No se pudo guardar el buzón'));
    } finally {
      setGuardando(false);
    }
  };

  // Función y no componente: un componente definido aquí dentro se volvería a montar en cada tecla
  const persona = ({ id, onElegir, textoNadie = 'Nadie', equipo = true }) => (
    <SelectorPersona formId={equipo && config.miembros?.length ? formId : null} valor={id ? personas[id] || { id } : null} permitirNadie textoNadie={textoNadie}
      onElegir={(p) => { conocer(p); onElegir(p?.id || null); }}
    >
      <button type="button" className="bz-persona-boton is-compacto">
        {id ? <Avatar persona={personas[id] || { nombre: '?' }} tam={20} /> : <UserRound className="h-4 w-4" aria-hidden="true" />}
        <span className="truncate">{id ? personas[id]?.nombre || 'Persona' : textoNadie}</span>
      </button>
    </SelectorPersona>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => !guardando && onOpenChange(v)}>
      <DialogContent className="sm:max-w-[720px] max-h-[92vh] flex flex-col bz-config">
        <DialogHeader>
          <DialogTitle>Buzón de casos</DialogTitle>
          <DialogDescription>
            Cada respuesta se convierte en un caso con folio, responsable, plazo e historial. Quien lo envía puede seguirlo y contestar.
          </DialogDescription>
        </DialogHeader>

        {carga.cargando && <div className="bz-centro py-10"><Loader2 className="h-6 w-6 od-spin" aria-label="Cargando" /></div>}
        {carga.error && <p className="bz-texto-peligro">{carga.error}</p>}

        {config && !carga.cargando && (
          <div className="bz-config-cuerpo od-scroll">
            <label className="bz-config-fila is-principal">
              <span>
                <b>Gestionar las respuestas como casos</b>
                <small>Al activarlo, las respuestas que ya existen también pasan a ser casos.</small>
              </span>
              <Switch checked={!!config.activo} onCheckedChange={(v) => set({ activo: v })} aria-label="Activar el buzón" />
            </label>

            <fieldset className="bz-config-grupo" disabled={!config.activo}>
              <legend>Folio y plazos</legend>
              <div className="bz-config-dos">
                <label className="bz-campo">
                  <span>Prefijo del folio</span>
                  <input value={config.prefijo || ''} maxLength={6} onChange={(e) => set({ prefijo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
                  <small>Ejemplo: {config.prefijo || 'QS'}-00012</small>
                </label>
                <label className="bz-campo">
                  <span>Plazo para resolver (días)</span>
                  <input type="number" min={0} max={365} value={config.dias_sla ?? 5} onChange={(e) => set({ dias_sla: Number(e.target.value) })} />
                  <small>0 = sin plazo. Cada categoría puede tener el suyo.</small>
                </label>
              </div>
            </fieldset>

            <fieldset className="bz-config-grupo" disabled={!config.activo}>
              <legend>Categorías</legend>
              <label className="bz-campo">
                <span>Pregunta que decide la categoría</span>
                <div className="flex gap-2 items-center">
                  <select className="bz-select flex-1" value={config.campo_categoria || ''} onChange={(e) => set({ campo_categoria: e.target.value || null })}>
                    <option value="">Ninguna: la decide Aurora IA o una persona</option>
                    {preguntasCategoria.map((p) => <option key={p.id} value={p.id}>{p.titulo}</option>)}
                  </select>
                  {config.campo_categoria && (
                    <button type="button" className="bz-boton-texto" onClick={categoriasDesdePregunta}>Usar sus opciones</button>
                  )}
                </div>
                <small>La opción elegida por quien responde se relaciona con la categoría del mismo nombre.</small>
              </label>
              <div className="bz-categorias">
                <div className="bz-categoria is-cabecera" aria-hidden="true">
                  <span /><span>Nombre</span><span>Responsable</span><span>Días</span><span />
                </div>
                {config.categorias.map((c, i) => (
                  <div key={c.id || i} className="bz-categoria">
                    <input type="color" value={c.color || '#2563eb'} onChange={(e) => setCategoria(i, { color: e.target.value })} aria-label={`Color de ${c.nombre}`} />
                    <input value={c.nombre} maxLength={60} onChange={(e) => setCategoria(i, { nombre: e.target.value })} aria-label="Nombre de la categoría" />
                    {persona({ id: c.responsable_id, textoNadie: 'Sin responsable', onElegir: (id) => setCategoria(i, { responsable_id: id }) })}
                    <input
                      type="number" min={0} max={365} placeholder={String(config.dias_sla ?? 5)}
                      value={c.dias_sla ?? ''} aria-label={`Días de plazo de ${c.nombre}`}
                      onChange={(e) => setCategoria(i, { dias_sla: e.target.value === '' ? null : Number(e.target.value) })}
                    />
                    <button type="button" className="od-tool-btn" onClick={() => set({ categorias: config.categorias.filter((_, j) => j !== i) })} aria-label={`Quitar ${c.nombre}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
                {config.categorias.length < 20 && (
                  <button
                    type="button"
                    className="bz-boton-texto"
                    onClick={() => set({ categorias: [...config.categorias, { nombre: '', color: COLORES[config.categorias.length % COLORES.length], responsable_id: null, dias_sla: null }] })}
                  >
                    <Plus className="h-4 w-4" /> Añadir categoría
                  </button>
                )}
              </div>
            </fieldset>

            <fieldset className="bz-config-grupo" disabled={!config.activo}>
              <legend>Asignación</legend>
              <div className="bz-reglas" role="radiogroup" aria-label="Cómo se reparten los casos">
                {REGLAS_ASIGNACION.map((r) => (
                  <label key={r.id} className={`bz-regla ${config.asignacion === r.id ? 'is-activa' : ''}`}>
                    <input type="radio" name="bz-regla" checked={config.asignacion === r.id} onChange={() => set({ asignacion: r.id })} />
                    <span><b>{r.nombre}</b><small>{r.detalle}</small></span>
                  </label>
                ))}
              </div>
              <div className="bz-config-fila">
                <span><b>Responsable por defecto</b><small>Recibe lo que ninguna regla asigna. Sin nadie, el caso espera sin asignar.</small></span>
                {persona({ id: config.responsable_defecto, textoNadie: 'Nadie', onElegir: (id) => set({ responsable_defecto: id }) })}
              </div>
              <div className="bz-campo">
                <span>Equipo del buzón</span>
                <div className="bz-equipo">
                  {(config.miembros || []).map((id) => (
                    <span key={id} className="bz-chip">
                      <Avatar persona={personas[id] || { nombre: '?' }} tam={18} /> {personas[id]?.nombre || 'Persona'}
                      <button type="button" onClick={() => set({ miembros: config.miembros.filter((m) => m !== id) })} aria-label={`Quitar a ${personas[id]?.nombre || 'persona'} del equipo`}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                  <SelectorPersona formId={null} valor={null} conGrupos={false} onElegir={(p) => { if (p && !config.miembros.includes(p.id)) { conocer(p); set({ miembros: [...config.miembros, p.id] }); } }}>
                    <button type="button" className="bz-boton-texto"><UserPlus className="h-4 w-4" /> Añadir persona</button>
                  </SelectorPersona>
                </div>
                <small>
                  {config.asignacion === 'rotativa'
                    ? 'Los casos se reparten por turnos entre estas personas.'
                    : 'Si hay equipo, solo estas personas pueden recibir casos. Vacío = cualquiera de la organización.'}
                </small>
              </div>
              <label className="bz-config-fila">
                <span><b>Quien tiene un caso puede pasarlo a otra persona</b><small>Con motivo, que queda en el historial.</small></span>
                <Switch checked={!!config.asignado_puede_reasignar} onCheckedChange={(v) => set({ asignado_puede_reasignar: v })} />
              </label>
              <label className="bz-config-fila">
                <span><b>Avisar por correo a quien recibe un caso</b><small>Además de la campana y la notificación del navegador.</small></span>
                <Switch checked={!!config.avisar_por_correo} onCheckedChange={(v) => set({ avisar_por_correo: v })} />
              </label>
            </fieldset>

            <fieldset className="bz-config-grupo" disabled={!config.activo}>
              <legend>Aurora IA</legend>
              <label className="bz-config-fila">
                <span>
                  <b>Clasificar cada caso al llegar</b>
                  <small>Resume el mensaje, sugiere categoría, prioridad y etiquetas. Solo rellena lo que nadie decidió; no lee nombres, correos ni teléfonos.</small>
                </span>
                <Switch checked={!!config.ia_triaje} onCheckedChange={(v) => set({ ia_triaje: v })} />
              </label>
            </fieldset>

            <fieldset className="bz-config-grupo" disabled={!config.activo}>
              <legend>Quien envía el caso</legend>
              <label className="bz-config-fila">
                <span><b>Seguimiento en línea</b><small>Al enviar recibe el folio y un enlace para ver en qué va su caso y escribir de nuevo.</small></span>
                <Switch checked={!!config.seguimiento_publico} onCheckedChange={(v) => set({ seguimiento_publico: v })} />
              </label>
              <label className="bz-config-fila">
                <span><b>Acuse de recibo por correo</b><small>Si dejó un correo, recibe el folio desde tu buzón.</small></span>
                <Switch checked={!!config.acuse_correo} onCheckedChange={(v) => set({ acuse_correo: v })} />
              </label>
              <label className="bz-campo">
                <span>Pregunta con el correo de contacto</span>
                <select className="bz-select" value={config.campo_correo || ''} onChange={(e) => set({ campo_correo: e.target.value || null })}>
                  <option value="">Solo el correo que recoge el formulario (Configuración → Recopilar correos)</option>
                  {preguntasCorreo.map((p) => <option key={p.id} value={p.id}>{p.titulo}</option>)}
                </select>
                <small>Para permitir mensajes anónimos: una pregunta de correo NO obligatoria.</small>
              </label>
            </fieldset>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={guardando}>Cancelar</Button>
          <Button onClick={guardar} disabled={!config || guardando}>
            {guardando && <Loader2 className="h-4 w-4 od-spin" />} Guardar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
